import { useNavigate } from 'react-router-dom';
import { History } from 'lucide-react';
import { Button } from '../ui/Button';
import { HistoryQueryForm } from './HistoryQueryForm';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { MOBILE_MEDIA_QUERY, NAV_ROUTES } from '../../utils/constants';
import type { Vehicle } from '../../types/vehicle';
import type { TripRoute } from '../../types/trip';

interface VehicleHistoryControlsProps {
  vehicles: Vehicle[];
  route: TripRoute | null;
  isLoading: boolean;
  error: Error | null;
  hasActiveQuery: boolean;
  onQuery: (vehicleId: string, from: string, to: string) => void;
  onClear: () => void;
  /** El panel del formulario está abierto. Lo controla quien monta el componente para que "Consulta" del panel de reproducción también pueda abrirlo. */
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * FE-25 — panel flotante sobre el mapa de Monitoreo (junto al selector de
 * estilo, que vive en `MapView.tsx` a la derecha): botón "Histórico de
 * recorrido" + el formulario de consulta.
 *
 * FE-27: el formulario se extrajo a `HistoryQueryForm` para compartirlo con la
 * página propia de mobile. Esta carcasa es la de desktop; en mobile
 * (`MOBILE_MEDIA_QUERY`) el botón no abre este panel: navega a la página propia.
 *
 * El botón usa `var(--accent)` sólido (como `Button variant="primary"`) y el
 * panel expandido usa `var(--bg-elevated)`/`var(--text-primary)` — los
 * mismos tokens del tema, para que se lea igual con la app en claro y en
 * oscuro. Antes el panel tenía un fondo oscuro fijo mientras el texto seguía
 * el tema: en claro salía texto oscuro sobre fondo oscuro, ilegible.
 */
export function VehicleHistoryControls({
  vehicles, route, isLoading, error, hasActiveQuery, onQuery, onClear, isOpen, onOpenChange,
}: VehicleHistoryControlsProps) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

  const handleClick = () => {
    if (isMobile) navigate(NAV_ROUTES.MONITORING_HISTORY);
    else onOpenChange(!isOpen);
  };

  return (
    <div className="monitoring-history">
      <Button
        variant="primary"
        size="sm"
        leftIcon={<History size={14} />}
        onClick={handleClick}
        title="Histórico de recorrido"
        style={{ boxShadow: 'var(--shadow-lg)', alignSelf: 'flex-start' }}
      >
        Histórico de recorrido
        {hasActiveQuery && !isOpen && (
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#FFFFFF', marginLeft: '6px', display: 'inline-block' }} />
        )}
      </Button>

      {/* `display: none`, no desmontar: el formulario guarda lo tecleado y
          cerrar el panel no debe borrarlo (antes vivía en este componente y
          sobrevivía al `isOpen`). */}
      <div className="monitoring-history__panel" style={isOpen ? undefined : { display: 'none' }}>
        <HistoryQueryForm
          vehicles={vehicles}
          route={route}
          isLoading={isLoading}
          error={error}
          hasActiveQuery={hasActiveQuery}
          onQuery={onQuery}
          onClear={onClear}
        />
      </div>
    </div>
  );
}
